import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { useFireRenderedSpinner } from '../../hooks/useProfileCardAnalytics';

import { CardPopupWrapper } from './CardPopupWrapper';

const styles = cssMap({
	inner: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	text: {
		marginTop: token('space.075'),
	},
	fill: {
		flex: '1',
		minWidth: '0px',
	},
	linkRow: {
		borderLeftColor: token('color.border'),
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderRightColor: token('color.border'),
		borderRightWidth: token('border.width'),
		borderRightStyle: 'solid',
		borderTopColor: token('color.border'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
	},
	linkRowFirst: {
		borderTopLeftRadius: token('radius.medium'),
		borderTopRightRadius: token('radius.medium'),
	},
	managerRow: {
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	viewAllRow: {
		borderBottomColor: token('color.border'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomLeftRadius: token('radius.medium'),
		borderBottomRightRadius: token('radius.medium'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
	},
});

function HeaderSkeleton() {
	return (
		<Stack space="space.075">
			<Inline spread="space-between" alignBlock="start">
				<Skeleton width="96px" height="96px" borderRadius={token('radius.full')} />
				<Inline space="space.100" alignBlock="center">
					<Skeleton width="32px" height="32px" borderRadius={token('radius.medium')} />
					<Skeleton width="96px" height="32px" borderRadius={token('radius.medium')} />
					<Skeleton width="32px" height="32px" borderRadius={token('radius.medium')} />
				</Inline>
			</Inline>
			<Stack space="space.050" xcss={styles.text}>
				<Skeleton width="45%" height="24px" borderRadius={token('radius.small')} />
				<Skeleton width="80%" height="16px" borderRadius={token('radius.small')} />
				<Skeleton width="50%" height="16px" borderRadius={token('radius.small')} />
				<Skeleton width="80%" height="20px" borderRadius={token('radius.small')} />
			</Stack>
		</Stack>
	);
}

function ManagerSectionSkeleton() {
	return (
		<Stack space="space.075">
			<Skeleton width="60px" height="24px" borderRadius={token('radius.small')} />
			<Inline space="space.100" alignBlock="center" xcss={styles.managerRow}>
				<Skeleton width="28px" height="28px" borderRadius={token('radius.full')} />
				<Stack space="space.050" xcss={styles.fill}>
					<Skeleton width="45%" height="14px" borderRadius={token('radius.small')} />
					<Skeleton width="65%" height="14px" borderRadius={token('radius.small')} />
				</Stack>
			</Inline>
		</Stack>
	);
}

function LinkRowSkeleton({ first }: { first?: boolean }) {
	return (
		<Box xcss={cx(styles.linkRow, first && styles.linkRowFirst)}>
			<Inline space="space.100" alignBlock="center" spread="space-between">
				<Inline space="space.100" alignBlock="center" xcss={styles.fill}>
					<Skeleton width="36px" height="36px" borderRadius={token('radius.large')} />
					<Stack xcss={styles.fill} space="space.050">
						<Skeleton width="40%" height="12px" borderRadius={token('radius.small')} />
						<Skeleton width="70%" height="12px" borderRadius={token('radius.small')} />
					</Stack>
				</Inline>
				<Skeleton width="32px" height="12px" borderRadius={token('radius.small')} />
			</Inline>
		</Box>
	);
}

function ViewAllRowSkeleton() {
	return (
		<Box xcss={cx(styles.linkRow, styles.viewAllRow)}>
			<Skeleton width="48px" height="20px" borderRadius={token('radius.small')} />
		</Box>
	);
}

function RecentWorkSectionSkeleton() {
	return (
		<Stack space="space.075">
			<Skeleton width="90px" height="20px" borderRadius={token('radius.small')} />
			<Stack space="space.0">
				<LinkRowSkeleton first />
				<LinkRowSkeleton />
				<LinkRowSkeleton />
				<ViewAllRowSkeleton />
			</Stack>
		</Stack>
	);
}

/**
 * Loading state shown while the profile card entry chunk resolves. Mirrors the
 * populated card layout:
 *
 * - Header: 96px avatar + action buttons (icon + "View profile") above the
 *   name + pronouns, subtitle (up to 2 lines), and location.
 * - Manager: heading + 24px avatar with name and job title lines.
 * - Recent work: heading + 3 bordered link rows that mirror the compact
 *   `LinkList` rendering used in `RecentWorkSection`
 *
 * Keeping the skeleton shape close to the populated layout minimises the
 * perceived layout jump when data resolves. Fires `rendered.spinner` on mount
 * so the event only emits when the spinner is actually on screen.
 */
export function CardLoading({ testId }: { testId?: string }): React.JSX.Element {
	useFireRenderedSpinner();
	return (
		<CardPopupWrapper>
			<Box testId={testId} role="status">
				<Stack space="space.150" xcss={styles.inner}>
					<HeaderSkeleton />
					<ManagerSectionSkeleton />
					<RecentWorkSectionSkeleton />
				</Stack>
			</Box>
		</CardPopupWrapper>
	);
}
